import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import { RxCross1 } from "react-icons/rx";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { HttpService } from "../../service/HttpService";
import { useAuth } from "../../context/AuthProvider";
import SelectInput from "../SelectInput/SelectInput";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";

function AddEditFullSwap({
  open,
  setOpen,
  id = "",
  editData = "",
  fetchFullswap,
  nutrients = [],
}) {
  const [units, setUnits] = useState([]);
  const [growth, setGrowth] = useState([]);
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);

  // const spotOptions = [
  //   { id: 20, name: 20 },
  //   { id: 40, name: 40 },
  // ];

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Recipe Name is required"),
    growthPhase: Yup.string().trim().required("Growth Phase is required"),
    ppm: Yup.number().typeError("PPM must be a number").required("PPM is required"),
    ec: Yup.number().typeError("EC must be a number").required("EC is required"),
    targetPh: Yup.number().typeError("Target PH must be a number").required("Target PH is required"),
    solutionTemp: Yup.number().typeError("Solution Temp must be a number").required("Solution Temp is required"),
    // spot: Yup.number().required("Spot is required"),
    ...nutrients.reduce((acc, nutrient) => {
      acc[`nutrient_${nutrient.id}`] = Yup.string();
      return acc;
    }, {})
  });
  const formik = useFormik({
    initialValues: {
      name: editData?.recipe_name || "",
      growthPhase: editData?.growth_phase_name || "",
      growthPhaseId: editData?.growth_phase_id || "",
      is_active : editData?.is_active || true,
      ppm: editData?.ppm || "",
      ec: editData?.ec || "",
      targetPh: editData?.target_ph || "",
      solutionTemp: editData?.solution_temp || "",
      // spot: editData?.spot || "",
      ...nutrients.reduce((acc, nutrient) => {
        acc[`nutrient_${nutrient.id}`] = editData?.[`nutrient_${nutrient.id}`] || "";
        acc[`unit_${nutrient.id}`] = editData?.[`unit_${nutrient.id}`] || nutrient.unit_id;
        return acc;
      }, {})
    },
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      try {
        const nutrientsData = nutrients.map(nutrient => ({
          nutrient_id: nutrient.id,
          nutrient_value: parseFloat(values[`nutrient_${nutrient.id}`]) || 0,
          unit_id: parseInt(values[`unit_${nutrient.id}`]) || nutrient.unit_id
        }));
        
        const payload = {
          recipe_name: values.name.trim(),
          growth_phase_id: parseInt(values.growthPhase),
          ppm: parseFloat(values.ppm),
          ec: parseFloat(values.ec),
          target_ph: parseFloat(values.targetPh),
          solution_temp: parseFloat(values.solutionTemp),
          // spot: parseInt(values.spot),
          nutrients: nutrientsData,
          is_active: values.is_active === "active" ? true : false
        };

        if (!id || id === "") {
          const response = await httpService.post(
            "full-swap/add-full-swap",
            payload
          );
          if (response) {
            toast.success("Full Swap Added Successfully", {
              position: "top-right",
            });
            formik.resetForm();
            setOpen(false);
            if (fetchFullswap) fetchFullswap();
          }
        } else {
          const response = await httpService.put(
            `full-swap/edit-full-swap/${id}`,
            payload
          );
          if (response) {
            toast.success("Full Swap Updated Successfully", {
              position: "top-right",
            });
            formik.resetForm();
            setOpen(false);
            if (fetchFullswap) fetchFullswap();
          }
        }
      } catch (error) {
        console.error("Error saving nutrient:", error);
        toast.error(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to save full swap",
          {
            position: "top-right",
          }
        );
      }
    },
  });

  const fetchUnits = () => {
    httpService
      .get("inventory/units")
      .then((response) => {
        setUnits(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const fetchGrowth = () => {
    httpService
      .get("growth-phase/get-growth-phase")
      .then((response) => {
        setGrowth(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  useEffect(() => {
    fetchUnits();
    fetchGrowth()
  }, [open]);

  useEffect(() => {
    if (open && nutrients.length > 0) {
      const formatValue = (val) => {
        if (!val && val !== 0) return "";
        return val % 1 === 0 ? parseInt(val) : parseFloat(val);
      };
      
      const initialValues = {
        name: editData?.recipe_name || "",
        growthPhase: editData?.growth_phase_id || editData?.growthPhase || "",
        is_active: editData?.is_active === true ? "active" : "inactive",
        growthPhaseId: editData?.growth_phase_id || editData?.growthPhase || "",
        ppm: formatValue(editData?.ppm),
        ec: formatValue(editData?.ec),
        targetPh: formatValue(editData?.target_ph),
        solutionTemp: formatValue(editData?.solution_temp),
        // spot: editData?.spot || "",
        ...nutrients.reduce((acc, nutrient) => {
          acc[`nutrient_${nutrient.id}`] = editData?.[`nutrient_${nutrient.id}`] || "";
          acc[`unit_${nutrient.id}`] = editData?.[`unit_${nutrient.id}`] || nutrient.unit_id;
          return acc;
        }, {})
      };
      formik.setValues(initialValues);
    }
  }, [open, nutrients, editData]);

  const hasAtLeastOneNutrientValue = nutrients.some((nutrient) => {
    const value = formik.values[`nutrient_${nutrient.id}`];
    if (value === null || value === undefined) return false;
    if (typeof value === "number") return !Number.isNaN(value);
    if (typeof value === "string") return value.trim() !== "";
    return false;
  });

  const isAssignDisabled = !hasAtLeastOneNutrientValue;

  return (
    <>
      <style>{`.no-label-select label { display: none; }`}</style>
      <Modal
        className="department-modal add-swap-modal"
        open={open}
        onClose={() => {
          formik.resetForm();
          setOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="modal_block">
            <div className="modal_header">
              <div className="modal_title_wrapper">
                <h2 className="modal_title">
                  {id ? "Edit Full Swap" : "Add Full Swap"}
                </h2>
              </div>
              <div className="cancel-btn-wrap">
                <RxCross1
                  onClick={() => {
                    formik.resetForm();
                    setOpen(false);
                  }}
                />
              </div>
            </div>
            <div className="modal_content">
              <div className="row align-items-center">
              {id && <div className="mb-3"> 
                <div className="col-md-3">
                  <div className="form_field_wrapper mb-0">
                    <label>
                      Status
                    </label>
                  </div>
                </div>

                <div className="col-md-9">
                  <FormControl fullWidth>
                    <RadioGroup
                      row
                      name="is_active"
                      value={formik.values.is_active}
                      onChange={(e) => formik.setFieldValue("is_active", e.target.value)}
                      style={{
                        justifyContent: "flex-start",
                        gap: "16px",
                        flexWrap: "nowrap",
                      }}
                    >
                      <FormControlLabel
                        value="active"
                        control={<Radio size="small" color="success" />}
                        label="Active"
                        style={{ margin: 0, fontSize: "12px" }}
                      />

                      <FormControlLabel
                        value="inactive"
                        control={<Radio size="small" color="error" />}
                        label="Inactive"
                        style={{ margin: 0, fontSize: "12px" }}
                      />
                    </RadioGroup>

                    {formik.touched.is_active && formik.errors.is_active && (
                      <span style={{ color: "red" }}>{formik.errors.is_active}</span>
                    )}
                  </FormControl>
                </div>
              </div>}
                <div className="col-md-3">
                  <div className="form_field_wrapper">
                    <label>
                      Name of Recipe
                      {!id && <span style={{ color: "red" }}>*</span>}
                    </label>
                  </div>
                </div>
                <div className="col-md-9">
                  <div className="form_field_wrapper">
                    <TextField
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      variant="outlined"
                      fullWidth
                      disabled={!!id}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <span style={{ color: "red" }}>{formik.errors.name}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row align-items-center">
                <div className="col-md-3">
                  <div className="form_field_wrapper">
                    <label>
                      Growth Phase
                      {!id && <span style={{ color: "red" }}>*</span>}
                    </label>
                  </div>
                </div>
                <div className="col-md-9">
                  <div className="form_field_wrapper no-label-select">
                    <SelectInput
                      name="growthPhase"
                      label={"Growth Phase"}
                      value={formik.values.growthPhase || ""}
                      options={growth}
                      multiple={false}
                      required={!id}
                      disabled={!!id}
                      onChange={(event) => {
                        formik.setFieldValue("growthPhase", event.target.value);
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.growthPhase && formik.errors.growthPhase}
                    />
                  </div>
                </div>
              </div>
                {/* <div className="row align-items-center">
                  <div className="col-md-4">
                    <div className="form_field_wrapper">
                      <label>
                        Spot
                        {!id && <span style={{ color: "red" }}>*</span>}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="form_field_wrapper no-label-select">
                      <SelectInput
                        name="spot"
                        label={"Spot"}
                        value={formik.values.spot || ""}
                        options={spotOptions}
                        multiple={false}
                        required={!id}
                        disabled={!!id}
                        onChange={(event) => {
                          formik.setFieldValue("spot", event.target.value);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.spot && formik.errors.spot}
                      />
                    </div>
                  </div>
              </div> */}
              <div className="row">
                <div className="col-md-12">
                  <div className="form_field_wrapper">
                    <label className="addedit-fontsize">
                      Target Parameters
                    </label>
                  </div>
                </div>
              </div>
                <div className="row align-items-center">
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                      <label>
                          PPM (500 Scale)
                          {!id && <span style={{ color: "red" }}>*</span>}
                        </label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                        <TextField
                          name="ppm"
                          value={formik.values.ppm}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          variant="outlined"
                          type="text"
                          fullWidth
                        />
                        {formik.touched.ppm && formik.errors.ppm && (
                          <span style={{ color: "red" }}>{formik.errors.ppm}</span>
                        )}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                      <label style={{ display: "block", textAlign: window.innerWidth > 767 ? "right" : "left" }}>
                          EC
                          {!id && <span style={{ color: "red" }}>*</span>}
                        </label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                        <TextField
                          name="ec"
                          value={formik.values.ec}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          variant="outlined"
                          type="text"
                          fullWidth
                        />
                        {formik.touched.ec && formik.errors.ec && (
                          <span style={{ color: "red" }}>{formik.errors.ec}</span>
                        )}
                    </div>
                  </div>
                </div>
              <div className="row align-items-center">
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                      <label>
                          Target PH
                          {!id && <span style={{ color: "red" }}>*</span>}
                        </label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                        <TextField
                          name="targetPh"
                          value={formik.values.targetPh}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          variant="outlined"
                          type="text"
                          fullWidth
                        />
                        {formik.touched.targetPh && formik.errors.targetPh && (
                          <span style={{ color: "red" }}>{formik.errors.targetPh}</span>
                        )}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                      <label style={{ display: "block", textAlign: window.innerWidth > 767 ? "right" : "left" }}>
                          Solution Temp
                          {!id && <span style={{ color: "red" }}>*</span>}
                        </label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                        <TextField
                          name="solutionTemp"
                          value={formik.values.solutionTemp}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          variant="outlined"
                          type="text"
                          fullWidth
                        />
                        {formik.touched.solutionTemp && formik.errors.solutionTemp && (
                          <span style={{ color: "red" }}>{formik.errors.solutionTemp}</span>
                        )}
                    </div>
                  </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="form_field_wrapper">
                     <label className="addedit-fontsize">
                      Nutrient Name
                    </label>
                  </div>
                </div>
              </div>
              {nutrients.map((nutrient) => (
                <div key={nutrient.id} className="row align-items-center">
                  <div className="col-md-3">
                    <div className="form_field_wrapper">
                      <label>
                        {nutrient.name}
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <TextField
                        name={`nutrient_${nutrient.id}`}
                        value={formik.values[`nutrient_${nutrient.id}`] || ""}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        variant="outlined"
                        fullWidth
                      />
                      {formik.touched[`nutrient_${nutrient.id}`] && formik.errors[`nutrient_${nutrient.id}`] && (
                        <span style={{ color: "red" }}>{formik.errors[`nutrient_${nutrient.id}`]}</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-3 no-label-select">
                    <div className="form_field_wrapper">
                      <SelectInput
                        name={`unit_${nutrient.id}`}
                        required={true}
                        label="Unit"
                        value={formik.values[`unit_${nutrient.id}`] || nutrient.unit_id}
                        options={units}
                        multiple={false}
                        onChange={(event) => {
                          formik.setFieldValue(`unit_${nutrient.id}`, event.target.value);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched[`unit_${nutrient.id}`] && formik.errors[`unit_${nutrient.id}`]}
                        displayEmpty
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="modal_footer">
              <button
                type="button"
                onClick={() => {
                  formik.resetForm();
                  setOpen(false);
                }}
                className="btn btn-border"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isAssignDisabled}
                style={{ cursor: isAssignDisabled ? "not-allowed" : "pointer" }}
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}

export default AddEditFullSwap;
